'use client';
import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default from 'react';
import PropTypes from 'prop-types';
import Button from '../Inputs/Button/Button.js';
import ButtonIcon from '../Inputs/Button/ButtonIcon/ButtonIcon.js';
import { BUTTON_VARIANT, BUTTON_TYPE } from '../Inputs/Button/Button.constants.js';
import { StyledPagination, PaginationLabel, List, ButtonGroupContainer } from './Pagination.styles.js';
import { getButtonBaseWidth, pagesToShow, getPaginationLabel } from './Pagination.helper.js';
import { PAGINATION_SIZE, PAGINATION_LABEL_POSITION, PAGINATION_ALIGNMENT, PAGINATION_BUTTON_GROUP_MIN_WIDTH, PAGINATION_MIN_WIDTH_THAT_SUPPORTS_EDGE_BUTTONS } from './Pagination.constants.js';
import useRect from '../../hooks/useRect.js';
import ButtonGroup from '../Inputs/ButtonGroup/index.js';

var Pagination = function Pagination(_ref) {
  var activePage = _ref.activePage,
    onChange = _ref.onChange,
    _ref$itemsPerPage = _ref.itemsPerPage,
    itemsPerPage = _ref$itemsPerPage === void 0 ? 10 : _ref$itemsPerPage,
    dataLength = _ref.dataLength,
    _ref$hasEdgePageButto = _ref.hasEdgePageButtons,
    hasEdgePageButtons = _ref$hasEdgePageButto === void 0 ? false : _ref$hasEdgePageButto,
    _ref$hasPageButtons = _ref.hasPageButtons,
    hasPageButtons = _ref$hasPageButtons === void 0 ? true : _ref$hasPageButtons,
    _ref$ariaLabel = _ref.ariaLabel,
    ariaLabel = _ref$ariaLabel === void 0 ? 'pagination' : _ref$ariaLabel,
    _ref$firstPageLabel = _ref.firstPageLabel,
    firstPageLabel = _ref$firstPageLabel === void 0 ? 'First Page' : _ref$firstPageLabel,
    _ref$previousPageLabe = _ref.previousPageLabel,
    previousPageLabel = _ref$previousPageLabe === void 0 ? 'Previous Page' : _ref$previousPageLabe,
    _ref$nextPageLabel = _ref.nextPageLabel,
    nextPageLabel = _ref$nextPageLabel === void 0 ? 'Next Page' : _ref$nextPageLabel,
    _ref$lastPageLabel = _ref.lastPageLabel,
    lastPageLabel = _ref$lastPageLabel === void 0 ? 'Last Page' : _ref$lastPageLabel,
    _ref$pageLabel = _ref.pageLabel,
    pageLabel = _ref$pageLabel === void 0 ? 'Page' : _ref$pageLabel,
    _ref$withLabel = _ref.withLabel,
    withLabel = _ref$withLabel === void 0 ? true : _ref$withLabel,
    _ref$labelText = _ref.labelText,
    labelText = _ref$labelText === void 0 ? 'of $ items' : _ref$labelText,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? PAGINATION_SIZE.REGULAR : _ref$size,
    _ref$labelPosition = _ref.labelPosition,
    labelPosition = _ref$labelPosition === void 0 ? PAGINATION_LABEL_POSITION.LEFT : _ref$labelPosition,
    _ref$alignment = _ref.alignment,
    alignment = _ref$alignment === void 0 ? PAGINATION_ALIGNMENT.RIGHT : _ref$alignment,
    _ref$maxVisiblePageBu = _ref.maxVisiblePageButtons,
    maxVisiblePageButtons = _ref$maxVisiblePageBu === void 0 ? 5 : _ref$maxVisiblePageBu,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? undefined : _ref$testId;
  var _useRect = useRect(),
    _useRect2 = _slicedToArray(_useRect, 2),
    containerRef = _useRect2[0],
    containerWidth = _useRect2[1].width;
  var _useRect3 = useRect(),
    _useRect4 = _slicedToArray(_useRect3, 2),
    contentRef = _useRect4[0],
    paginationWidth = _useRect4[1].width;
  var _useRect5 = useRect(),
    _useRect6 = _slicedToArray(_useRect5, 2),
    buttonContainerRef = _useRect6[0],
    buttonContainerWidth = _useRect6[1].width;
  var totalPageCount = Math.ceil(dataLength / itemsPerPage);
  var dynamicWidth = Math.min(containerWidth - (paginationWidth - buttonContainerWidth), PAGINATION_BUTTON_GROUP_MIN_WIDTH[size] * maxVisiblePageButtons);
  var buttonGroupMinWidth = PAGINATION_BUTTON_GROUP_MIN_WIDTH[size];
  var buttonGroupWidth = Math.max(dynamicWidth, buttonGroupMinWidth);
  var pagesToRender = Math.floor(buttonGroupWidth / getButtonBaseWidth(size, activePage));
  var pages = pagesToShow({
    currentPage: activePage,
    lastPage: totalPageCount,
    pagesToRender: pagesToRender
  });
  var isFirstPage = activePage <= 1;
  var isLastPage = activePage >= totalPageCount;
  var paginationLabel = getPaginationLabel(dataLength, itemsPerPage, activePage, labelText);
  var canFitEdgeButtons = containerWidth > PAGINATION_MIN_WIDTH_THAT_SUPPORTS_EDGE_BUTTONS[size];
  return React__default.createElement("div", {
    ref: containerRef,
    "data-testid": testId
  }, React__default.createElement(StyledPagination, {
    "aria-label": ariaLabel,
    $size: size,
    $labelPosition: labelPosition,
    $alignment: alignment,
    ref: contentRef
  }, withLabel && React__default.createElement(PaginationLabel, {
    $size: size
  }, paginationLabel), React__default.createElement(List, {
    $size: size
  }, hasEdgePageButtons && canFitEdgeButtons && React__default.createElement("li", null, React__default.createElement(ButtonIcon, {
    ariaLabel: firstPageLabel,
    title: firstPageLabel,
    disabled: isFirstPage,
    onClick: function onClick() {
      return onChange({
        pageSelected: 1
      });
    },
    icon: "First",
    variant: BUTTON_VARIANT.SECONDARY,
    size: size
  }, firstPageLabel)), React__default.createElement("li", null, React__default.createElement(ButtonIcon, {
    ariaLabel: previousPageLabel,
    title: previousPageLabel,
    disabled: isFirstPage,
    onClick: function onClick() {
      return onChange({
        pageSelected: activePage - 1
      });
    },
    icon: "ChevronLeft",
    variant: BUTTON_VARIANT.SECONDARY,
    size: size
  }, previousPageLabel)), hasPageButtons && React__default.createElement(ButtonGroupContainer, {
    ref: buttonContainerRef,
    $size: size,
    $hasFixedWidth: totalPageCount >= pagesToRender,
    $buttonGroupWidth: buttonGroupWidth
  }, React__default.createElement(ButtonGroup, null, pages.map(function (pageNumber) {
    return React__default.createElement(Button, {
      key: pageNumber,
      ariaLabel: "".concat(pageLabel, " ").concat(pageNumber),
      type: BUTTON_TYPE.BUTTON,
      variant: pageNumber === activePage ? BUTTON_VARIANT.PRIMARY : BUTTON_VARIANT.SECONDARY,
      onClick: function onClick() {
        return onChange({
          pageSelected: pageNumber
        });
      },
      size: size,
      title: "".concat(pageLabel, " ").concat(pageNumber)
    }, pageNumber);
  }))), React__default.createElement("li", null, React__default.createElement(ButtonIcon, {
    ariaLabel: nextPageLabel,
    title: nextPageLabel,
    onClick: function onClick() {
      return onChange({
        pageSelected: activePage + 1
      });
    },
    disabled: isLastPage,
    icon: "ChevronRight",
    variant: BUTTON_VARIANT.SECONDARY,
    size: size
  }, nextPageLabel)), hasEdgePageButtons && canFitEdgeButtons && React__default.createElement("li", null, React__default.createElement(ButtonIcon, {
    ariaLabel: lastPageLabel,
    title: lastPageLabel,
    disabled: isLastPage,
    onClick: function onClick() {
      return onChange({
        pageSelected: totalPageCount
      });
    },
    icon: "Last",
    variant: BUTTON_VARIANT.SECONDARY,
    size: size
  }, lastPageLabel)))));
};
Pagination.propTypes = {
  activePage: PropTypes.number.isRequired,
  alignment: PropTypes.string,
  ariaLabel: PropTypes.string,
  dataLength: PropTypes.number.isRequired,
  firstPageLabel: PropTypes.string,
  hasEdgePageButtons: PropTypes.bool,
  hasPageButtons: PropTypes.bool,
  itemsPerPage: PropTypes.number,
  labelPosition: PropTypes.oneOf(Object.values(PAGINATION_LABEL_POSITION)),
  labelText: PropTypes.string,
  lastPageLabel: PropTypes.string,
  maxVisiblePageButtons: PropTypes.number,
  nextPageLabel: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  pageLabel: PropTypes.string,
  previousPageLabel: PropTypes.string,
  size: PropTypes.oneOf(Object.values(PAGINATION_SIZE)),
  testId: PropTypes.string,
  withLabel: PropTypes.bool
};

export { Pagination, Pagination as default };
//# sourceMappingURL=Pagination.js.map
